/**
 * M5/3: 2D taktikai csata visszajátszó nézet (PixiJS 8).
 * Determinisztikus: ugyanaz a battle-log + replay-data → ugyanaz az animáció.
 * M11: a megjelenítés a közös BattleScene-ben él; minden effekt a battle-log tényleges eseményéből származik,
 * kereséskor (seek) csendes állapot-szinkron fut.
 * Track 2 (filmes lejátszó): automatikus kamera, lassítás a kilövéseknél, 0.5×–4× sebesség, idővonal kilövés-jelölőkkel,
 * megosztható nyilvános link és (ha a böngésző tudja) rövid videóklip-mentés. A tiszta logika a replay/timeline.ts-ben él.
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as PIXI from 'pixi.js';
import 'pixi.js/unsafe-eval';
import type { BattleReplay, ReplayShip } from '@galaxis/shared';
import { BattleScene } from '../pixi/battle-scene.ts';
import { shareBattleReport, unshareBattleReport } from '../api/galaxy.ts';
import { audio } from '../audio/index.ts';
import {
  stateAt, lastTick, killTicks, computeFocus, recentActors, slowMoFactor, REPLAY_SPEEDS, type ReplaySpeed,
} from '../replay/timeline.ts';

const CANVAS_W = 640;
const CANVAS_H = 360;
const ATTACKER_X = 140;
const DEFENDER_X = 500;
const SHIP_SPACING = 52;
const TICK_DURATION_MS = 300; // 1× sebesség
const SLOWMO_MS = 900;

interface BattleViewProps {
  replay: BattleReplay;
  onClose: () => void;
  /** a csata azonosítója – ha megvan, megjelenik a Megosztás gomb (a nyilvános oldalon nincs) */
  shareBattleId?: string;
  /** automatikus lejátszás indulásnál (megosztott linkről érkezőknek) */
  autoPlay?: boolean;
}

export function BattleView({ replay, onClose, shareBattleId, autoPlay }: BattleViewProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<BattleScene | null>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const tickRef = useRef(0);
  const maxTick = useMemo(() => lastTick(replay.log), [replay.log]);
  const kills = useMemo(() => killTicks(replay.log), [replay.log]);
  const animFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const accRef = useRef<number>(0);
  const slow = useRef({ value: 1, until: 0, factor: 1 });
  const cinematicRef = useRef(true);

  const [playing, setPlaying] = useState(false);
  const [currentTick, setCurrentTick] = useState(0);
  const [speed, setSpeed] = useState<ReplaySpeed>(1);
  const [cinematic, setCinematic] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  cinematicRef.current = cinematic;

  // ---- PixiJS init ----
  useEffect(() => {
    let app: PIXI.Application | null = null;
    let destroyed = false;
    // Pixi 8 throws if destroy() runs before init() resolves (StrictMode unmounts immediately in dev)
    let ready = false;

    async function init() {
      if (!canvasRef.current) return;
      app = new PIXI.Application();
      await app.init({
        width: CANVAS_W,
        height: CANVAS_H,
        backgroundColor: 0x030712,
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
      });
      if (destroyed) { app.destroy(true); return; }
      ready = true;
      appRef.current = app;
      canvasRef.current.appendChild(app.canvas);
      // Scale the fixed-size arena to the panel width (panels are 288–360 px wide)
      Object.assign(app.canvas.style, { display: 'block', width: '100%', height: '100%' });

      const scene = new BattleScene(app.renderer, app.stage, CANVAS_W, CANVAS_H);
      sceneRef.current = scene;
      // frame-time guard: on a slow phone the scene lowers its own effect level; also cap the render rate
      scene.onQualityChange = q => { if (q === 'low' && app) app.ticker.maxFPS = 30; };
      app.ticker.add(t => scene.update(t.deltaMS));

      const makeLabel = (text: string, x: number, color: number) => {
        const t = new PIXI.Text({ text, style: { fontSize: 11, fill: color, fontFamily: 'monospace', letterSpacing: 2, stroke: { color: 0x030712, width: 3, join: 'round' } }, resolution: 2 });
        t.anchor.set(0.5, 0);
        t.x = x;
        t.y = 8;
        app!.stage.addChild(t);
      };
      makeLabel('TÁMADÓ', ATTACKER_X, 0x7dd3fc);
      makeLabel('VÉDŐ', DEFENDER_X, 0xfb7185);

      const place = (list: ReplayShip[], x: number) =>
        list.forEach((s, i) => scene.addShip(s, x, CANVAS_H / 2 + (i - (list.length - 1) / 2) * SHIP_SPACING));
      place(replay.replayData.ships.filter(s => s.side === 'attacker'), ATTACKER_X);
      place(replay.replayData.ships.filter(s => s.side === 'defender'), DEFENDER_X);

      setInitialized(true);
    }

    // Deferred so StrictMode's throwaway mount never creates (and instantly destroys) a WebGL app
    const initTimer = setTimeout(() => { if (!destroyed) void init(); }, 0);
    return () => {
      clearTimeout(initTimer);
      destroyed = true;
      animFrameRef.current && cancelAnimationFrame(animFrameRef.current);
      if (recorder.current?.state === 'recording') recorder.current.stop();
      sceneRef.current?.destroyScene();
      sceneRef.current = null;
      appRef.current = null;
      if (ready) app?.destroy(true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Silent state sync (seek / restart): no effects, just the state at `targetTick` ----
  const applyEventsUpTo = useCallback((targetTick: number) => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.clearEffects();
    scene.resetCamera();
    scene.timeScale = 1;
    slow.current = { value: 1, until: 0, factor: 1 };
    for (const s of stateAt(replay.replayData.ships, replay.log, targetTick).values()) {
      scene.setState(s.ship.id, s.currentHull, s.currentShield, s.destroyed);
    }
  }, [replay.log, replay.replayData.ships]);

  // ---- the camera follows the action of the current tick ----
  const aimCamera = useCallback((tick: number, killedIds: string[]) => {
    const scene = sceneRef.current;
    if (!scene) return;
    if (!cinematicRef.current) { scene.setCameraTarget(null); return; }
    const ids = killedIds.length ? killedIds : recentActors(replay.log, tick, 2);
    const pts = ids.map(id => scene.shipPos(id)).filter((p): p is { x: number; y: number } => !!p);
    scene.setCameraTarget(pts.length ? computeFocus(pts, { w: CANVAS_W, h: CANVAS_H }, killedIds.length ? { maxZoom: 2.3, padding: 70 } : {}) : null);
  }, [replay.log]);

  useEffect(() => { if (!cinematic) sceneRef.current?.setCameraTarget(null); else aimCamera(tickRef.current, []); }, [cinematic, aimCamera]);

  // ---- Animation loop: the real events of each tick drive the effects ----
  useEffect(() => {
    if (!initialized) return;

    const loop = (time: number) => {
      animFrameRef.current = requestAnimationFrame(loop);
      const delta = Math.min(100, time - lastTimeRef.current);
      lastTimeRef.current = time;
      const scene = sceneRef.current;
      const sl = slow.current;

      // slow motion eases in fast and out gently, in real time
      const target = time < sl.until ? sl.factor : 1;
      sl.value += (target - sl.value) * Math.min(1, delta / (target < sl.value ? 60 : 280));
      if (scene) scene.timeScale = sl.value;

      if (!playing) return;
      accRef.current += delta * speed * sl.value;

      while (accRef.current >= TICK_DURATION_MS) {
        accRef.current -= TICK_DURATION_MS;
        tickRef.current = Math.min(tickRef.current + 1, maxTick);
        setCurrentTick(tickRef.current);

        if (scene) {
          const killed: string[] = [];
          for (const ev of replay.log) {
            if (ev.tick !== tickRef.current || !ev.targetId) continue;
            if (ev.type === 'attack') scene.fire(ev.attackerId, ev.targetId, ev.shieldDamage ?? 0, ev.hullDamage ?? 0);
            else if (ev.type === 'shield_break') scene.shieldBreak(ev.targetId);
            else if (ev.type === 'ship_destroyed') { scene.destroy(ev.targetId); killed.push(ev.targetId); }
          }
          aimCamera(tickRef.current, killed);
          if (killed.length && cinematicRef.current && !scene.reduced) {
            sl.factor = slowMoFactor(killed.length, tickRef.current >= maxTick || kills[kills.length - 1] === tickRef.current);
            sl.until = time + SLOWMO_MS;
          }
          if (tickRef.current >= maxTick) {
            const won = replay.winner;
            if (won && won !== 'draw') audio.play('victory', { vol: 0.8 });
          }
        }

        if (tickRef.current >= maxTick) {
          setPlaying(false);
          if (recorder.current?.state === 'recording') setTimeout(() => recorder.current?.stop(), 1200);
          scene?.setCameraTarget(null);
          break;
        }
      }
    };

    lastTimeRef.current = performance.now();
    accRef.current = 0;
    animFrameRef.current = requestAnimationFrame(loop);
    return () => {
      animFrameRef.current && cancelAnimationFrame(animFrameRef.current);
    };
  }, [initialized, playing, speed, maxTick, replay.log, replay.winner, aimCamera, kills]);

  // shared links start by themselves (the click that opened the page already unlocked audio where allowed)
  useEffect(() => { if (initialized && autoPlay) setPlaying(true); }, [initialized, autoPlay]);

  // ---- Handlers ----
  const handlePlayPause = () => {
    if (currentTick >= maxTick && !playing) {
      tickRef.current = 0;
      setCurrentTick(0);
      applyEventsUpTo(0);
    }
    lastTimeRef.current = performance.now();
    accRef.current = 0;
    setPlaying(p => !p);
  };

  const handleRestart = () => {
    setPlaying(false);
    tickRef.current = 0;
    setCurrentTick(0);
    applyEventsUpTo(0);
  };

  const handleSeek = (tick: number) => {
    setPlaying(false);
    tickRef.current = tick;
    setCurrentTick(tick);
    applyEventsUpTo(tick);
  };

  const handleShare = async () => {
    if (!shareBattleId) return;
    setShareError(null);
    try {
      const { path } = await shareBattleReport(shareBattleId);
      const url = `${window.location.origin}${path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); } catch { /* the link stays visible to copy by hand */ }
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'A megosztás nem sikerült.');
    }
  };

  const handleUnshare = async () => {
    if (!shareBattleId) return;
    try { await unshareBattleReport(shareBattleId); setShareUrl(null); } catch { setShareError('A link visszavonása nem sikerült.'); }
  };

  // Optional clip: records the canvas while the replay plays from the start, then offers a .webm download
  const canRecord = typeof MediaRecorder !== 'undefined' && typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;
  const handleRecord = () => {
    const canvas = appRef.current?.canvas as HTMLCanvasElement | undefined;
    if (!canvas || recording) return;
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(canvas.captureStream(30), { mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm' });
    rec.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      setRecording(false);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob(chunks, { type: 'video/webm' }));
      a.download = 'galaxis-csata.webm';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };
    recorder.current = rec;
    rec.start();
    setRecording(true);
    tickRef.current = 0; setCurrentTick(0); applyEventsUpTo(0);
    lastTimeRef.current = performance.now(); accRef.current = 0;
    setPlaying(true);
  };

  const progressPercent = maxTick > 0 ? (currentTick / maxTick) * 100 : 0;
  const ended = currentTick >= maxTick;
  const btn = 'text-xs px-2 py-1 rounded transition-colors min-h-[32px]';

  return (
    <div className="flex flex-col gap-2" data-testid="battle-replay">
      {/* Canvas container */}
      <div
        className="relative rounded-lg overflow-hidden"
        style={{ border: '1px solid rgba(100,116,139,0.3)', background: '#030712' }}
      >
        <div ref={canvasRef} data-testid="replay-canvas" style={{ width: '100%', aspectRatio: `${CANVAS_W}/${CANVAS_H}` }} />
        <button
          onClick={onClose}
          aria-label="Bezárás"
          className="absolute top-2 right-2 text-slate-500 hover:text-slate-300 text-lg leading-none"
          style={{ background: 'rgba(15,23,42,0.8)', borderRadius: 4, width: 44, height: 44 }}
        >
          ×
        </button>
        {recording && <span className="absolute top-2 left-2 text-xs font-mono text-red-400 animate-pulse">● REC</span>}
        {/* Winner overlay */}
        {ended && replay.winner && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            data-testid="replay-winner"
          >
            <span className="text-2xl font-bold text-slate-100" style={{ textShadow: '0 0 20px #60a5fa' }}>
              {replay.winner === 'attacker' ? '⚔️ Támadó győzött' :
               replay.winner === 'defender' ? '🛡️ Védő győzött' : '⚖️ Döntetlen'}
            </span>
          </div>
        )}
      </div>

      {/* Timeline scrubber with kill markers */}
      <div className="px-1">
        <div className="relative">
          <input
            type="range"
            min={0}
            max={maxTick}
            value={currentTick}
            onChange={e => handleSeek(Number(e.target.value))}
            aria-label="Idővonal"
            data-testid="replay-scrubber"
            className="w-full h-1 rounded cursor-pointer"
            style={{ accentColor: '#60a5fa' }}
          />
          {maxTick > 0 && kills.map(k => (
            <span key={k} aria-hidden className="absolute -top-1 w-0.5 h-2.5 pointer-events-none" style={{ left: `calc(${(k / maxTick) * 100}% - 1px)`, background: '#f87171' }} />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-600 font-mono mt-0.5">
          <span data-testid="replay-tick">tick {currentTick}</span>
          <span>{kills.length ? `${kills.length} kilövés · ` : ''}{maxTick}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <button onClick={handleRestart} aria-label="Újraindítás" className={`${btn} text-slate-400 hover:text-slate-200`} style={{ border: '1px solid rgba(100,116,139,0.3)' }}>⏮</button>
        <button
          onClick={handlePlayPause}
          data-testid="replay-play"
          className={`${btn} px-3 text-slate-200 hover:text-white font-medium`}
          style={{ border: '1px solid rgba(100,116,139,0.4)', background: 'rgba(96,165,250,0.1)' }}
        >
          {playing ? '⏸ Szünet' : ended ? '▶ Újra' : '▶ Lejátszás'}
        </button>
        <button
          onClick={() => setCinematic(c => !c)}
          aria-pressed={cinematic}
          data-testid="replay-cinematic"
          title="Automatikus kamera és lassítás a kilövéseknél"
          className={btn}
          style={{ border: `1px solid ${cinematic ? 'rgba(96,165,250,0.6)' : 'rgba(100,116,139,0.3)'}`, color: cinematic ? '#60a5fa' : '#94a3b8', background: cinematic ? 'rgba(96,165,250,0.1)' : 'transparent' }}
        >
          🎥 Filmes
        </button>
        <div className="flex gap-1 ml-auto" role="group" aria-label="Sebesség">
          {REPLAY_SPEEDS.map(s => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              aria-pressed={speed === s}
              className={btn}
              style={{
                border: `1px solid ${speed === s ? 'rgba(96,165,250,0.6)' : 'rgba(100,116,139,0.3)'}`,
                color: speed === s ? '#60a5fa' : '#94a3b8',
                background: speed === s ? 'rgba(96,165,250,0.1)' : 'transparent',
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      {(shareBattleId || canRecord) && (
        <div className="flex flex-wrap items-center gap-2 px-1">
          {shareBattleId && (
            <button onClick={handleShare} data-testid="replay-share" className={`${btn} text-slate-200`} style={{ border: '1px solid rgba(100,116,139,0.4)' }}>🔗 Megosztás</button>
          )}
          {canRecord && (
            <button onClick={handleRecord} disabled={recording} data-testid="replay-record" className={`${btn} text-slate-300 disabled:opacity-50`} style={{ border: '1px solid rgba(100,116,139,0.3)' }}>⏺ Klip (.webm)</button>
          )}
        </div>
      )}
      {shareUrl && (
        <div className="px-1 flex items-center gap-2 text-xs" data-testid="replay-share-result">
          <input readOnly value={shareUrl} aria-label="Megosztási link" onFocus={e => e.currentTarget.select()}
            className="flex-1 min-w-0 bg-slate-900/70 rounded px-2 py-1 font-mono text-slate-300" style={{ border: '1px solid rgba(100,116,139,0.3)' }} />
          <button onClick={handleUnshare} className={`${btn} text-red-300`} style={{ border: '1px solid rgba(248,113,113,0.3)' }}>Visszavonás</button>
        </div>
      )}
      {shareError && <p role="alert" className="px-1 text-xs text-red-400">{shareError}</p>}
      {shareUrl && <p className="px-1 text-[11px] text-slate-500">A link olvasható, bejelentkezés nélkül megnyitható; nem tartalmaz hajó- vagy flottaneveket, csak a csata lefolyását.</p>}

      {/* Progress bar */}
      <div className="h-1 rounded overflow-hidden mx-1" style={{ background: 'rgba(30,41,59,0.8)' }}>
        <div
          className="h-full transition-all"
          style={{ width: `${progressPercent}%`, background: 'linear-gradient(to right, #1d4ed8, #60a5fa)' }}
        />
      </div>
    </div>
  );
}
